/**
 * Where the screener's hover card goes, and the shapes it draws.
 *
 * **Toward the charts.** The side is read off the screener's own box rather
 * than from the layout, for `screenerPlacement`'s reason: a stored side is a
 * second answer a divider drag can falsify. A screener spanning most of the
 * width is docked top or bottom, and the card opens below or above it; else it
 * is a column, and the card opens on the side with more room.
 */

export type CardSide = "left" | "right" | "above" | "below";

interface Box {
  left: number;
  top: number;
  width: number;
  height: number;
}

/** A screener wider than this share of the viewport is a top/bottom dock. */
const ROW_DOCK_SHARE = 0.7;
/** Gap between the cell (or the screener's edge) and the card. */
const GAP = 8;

export function cardSide(screener: Box, viewport: { width: number; height: number }): CardSide {
  if (screener.width > viewport.width * ROW_DOCK_SHARE) {
    return screener.top + screener.height / 2 < viewport.height / 2 ? "below" : "above";
  }
  return screener.left + screener.width / 2 > viewport.width / 2 ? "left" : "right";
}

/**
 * The card's top-left, in viewport pixels: off the screener's edge on `side`,
 * level with the hovered cell, and clamped inside the viewport.
 */
export function cardPosition(
  side: CardSide,
  cell: Box,
  screener: Box,
  card: { width: number; height: number },
  viewport: { width: number; height: number },
): { left: number; top: number } {
  let left: number;
  let top: number;
  switch (side) {
    case "left":
      left = screener.left - GAP - card.width;
      top = cell.top + cell.height / 2 - card.height / 2;
      break;
    case "right":
      left = screener.left + screener.width + GAP;
      top = cell.top + cell.height / 2 - card.height / 2;
      break;
    case "above":
      left = cell.left;
      top = screener.top - GAP - card.height;
      break;
    case "below":
      left = cell.left;
      top = screener.top + screener.height + GAP;
      break;
  }
  const clamp = (v: number, max: number) => Math.max(GAP, Math.min(v, max - GAP));
  return {
    left: clamp(left, viewport.width - card.width),
    top: clamp(top, viewport.height - card.height),
  };
}

/** An SVG polyline's points for `values` in a `width` × `height` box, top = max. */
export function linePoints(values: readonly number[], width: number, height: number): string {
  const finite = values.filter(Number.isFinite);
  if (finite.length === 0) return "";
  const min = Math.min(...finite);
  const max = Math.max(...finite);
  const span = max - min || 1;
  const step = values.length > 1 ? width / (values.length - 1) : 0;
  return values
    .map((v, i) => (Number.isFinite(v) ? `${(i * step).toFixed(1)},${(height - ((v - min) / span) * height).toFixed(1)}` : ""))
    .filter(Boolean)
    .join(" ");
}
